import React, { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
	ArrowRightCircle,
	IdCard,
	ArrowLeft,
	ShieldCheck,
	Brain,
} from 'lucide-react';
import {
	Button,
	InputWrapperAuth,
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui';
import {
	useCreateProfessional,
	useGetClinics,
	useGetProfessionalProfession,
} from '@/services';
import {
	clearStepData,
	getStepData,
	saveStepData,
} from '@/utils/localStorageUtils';
import { STORAGE_KEYS } from './StepOne';
import { useNavigate } from 'react-router-dom';
import { setProfessionalData } from '@/utils/cookies';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/pages/public/landing/components/Header';
import {
	APPROACHES,
	getApproach,
	isPsychologyServiceName,
	type PsychologyApproach,
} from '@/constants/professionalTags';

const APPROACH_KEYS = APPROACHES.map((a) => a.key) as [
	PsychologyApproach,
	...PsychologyApproach[],
];

/**
 * A obrigatoriedade da abordagem depende do serviço escolhido na etapa 2
 * (só psicólogos). O schema lê a flag em tempo de validação via ref.
 */
const buildSchema = (requireMainApproach: { current: boolean }) =>
	z
		.object({
			service_modality: z.enum([
				'FULL_REMOTE',
				'HYBRID',
				'FULL_PRESENCIAL',
			]),
			clinic_id: z.string().optional(),
			main_approach: z.enum(APPROACH_KEYS).optional().or(z.literal('')),
		})
		.superRefine((data, ctx) => {
			if (data.service_modality !== 'FULL_REMOTE' && !data.clinic_id) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message:
						'Clínica é obrigatória para modalidades híbrida ou presencial',
					path: ['clinic_id'],
				});
			}
			if (requireMainApproach.current && !data.main_approach) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message:
						'Psicólogos precisam informar a abordagem principal',
					path: ['main_approach'],
				});
			}
		});

type FormData = z.infer<ReturnType<typeof buildSchema>>;

// Componente de Label interativo padronizado
const FormLabel = ({
	children,
	htmlFor,
}: {
	children: React.ReactNode;
	htmlFor?: string;
}) => (
	<label
		htmlFor={htmlFor}
		className="mb-1 ml-1 block cursor-pointer text-[10px] font-black uppercase tracking-widest text-stone-500 transition-colors hover:text-stone-700"
	>
		{children}
	</label>
);

const SlidingToggle = ({
	options,
	value,
	onChange,
}: {
	options: { value: string; label?: string; children?: React.ReactNode }[];
	value?: string;
	onChange: (val: string) => void;
}) => {
	const activeIndex = options.findIndex((o) => o.value === value);
	const count = options.length;

	return (
		<div className="w-full space-y-3">
			<div className="relative flex w-full rounded-lg bg-stone-100 p-1">
				<div
					className="absolute bottom-1 top-1 rounded-md bg-white shadow-sm transition-all duration-300 ease-[cubic-bezier(0.25,1,0.5,1)]"
					style={{
						width: `calc((100% - 8px) / ${count})`,
						left: `calc(4px + ${activeIndex} * ((100% - 8px) / ${count}))`,
					}}
				/>
				{options.map((opt) => (
					<button
						key={opt.value}
						type="button"
						onClick={() => onChange(opt.value)}
						className={`
							z-10 flex-1 cursor-pointer rounded-md border-none bg-transparent px-0 py-1.5 text-center text-xs font-bold outline-none ring-0
							transition-colors duration-200 focus:outline-none focus:ring-0
							${
								value === opt.value
									? 'text-stone-900'
									: 'text-stone-400 hover:text-stone-600'
							}
						`}
					>
						{opt.label || opt.value}
					</button>
				))}
			</div>
		</div>
	);
};

const StepFour = () => {
	const navigate = useNavigate();
	const requireMainApproachRef = useRef(false);
	const schema = useMemo(() => buildSchema(requireMainApproachRef), []);
	const {
		setValue,
		handleSubmit,
		watch,
		clearErrors,
		formState: { errors },
	} = useForm<FormData>({
		resolver: zodResolver(schema),
		defaultValues: {
			service_modality: 'FULL_REMOTE',
			main_approach: '',
		},
	});

	const { data } = useGetClinics({
		status: 'true',
	});

	// Serviço escolhido na etapa 2: define se a abordagem principal é obrigatória.
	const { data: professionsData } = useGetProfessionalProfession();
	const selectedProfessionId: string | undefined = getStepData(
		STORAGE_KEYS.PROFESSIONAL_AUTH,
		STORAGE_KEYS.STEP_DOCUMENTATION,
	)?.profession;
	const selectedProfession = professionsData?.find(
		(p) => p.id === selectedProfessionId,
	);
	const isPsychology = isPsychologyServiceName(
		selectedProfession?.service_name,
		selectedProfession?.professional_label,
	);
	requireMainApproachRef.current = isPsychology;

	useEffect(() => {
		const savedData = getStepData(
			STORAGE_KEYS.PROFESSIONAL_AUTH,
			STORAGE_KEYS.STEP_MODALITY,
		);
		if (savedData) {
			Object.entries(savedData).forEach(([key, value]) =>
				setValue(key as keyof FormData, value as any),
			);
		}
	}, [setValue, data]);

	const { mutateAsync } = useCreateProfessional(() => {
		clearStepData(STORAGE_KEYS.PROFESSIONAL_AUTH);
		navigate('/profissional/autorizacao');
	});

	const onSubmit = async (dataForm: FormData) => {
		saveStepData(
			STORAGE_KEYS.PROFESSIONAL_AUTH,
			STORAGE_KEYS.STEP_MODALITY,
			dataForm,
		);

		const personalData = getStepData(
			STORAGE_KEYS.PROFESSIONAL_AUTH,
			STORAGE_KEYS.STEP_PERSONAL,
		);
		const documentationData = getStepData(
			STORAGE_KEYS.PROFESSIONAL_AUTH,
			STORAGE_KEYS.STEP_DOCUMENTATION,
		);
		const contactData = getStepData(
			STORAGE_KEYS.PROFESSIONAL_AUTH,
			STORAGE_KEYS.STEP_CONTACT,
		);
		const modalityData = getStepData(
			STORAGE_KEYS.PROFESSIONAL_AUTH,
			STORAGE_KEYS.STEP_MODALITY,
		);

		// Montagem do payload seguindo o novo padrão solicitado
		const prefix = String(personalData.name_prefix ?? '')
			.trim()
			.slice(0, 32);
		const phoneNumber = String(contactData.phone_number ?? '').trim();
		const lattesUrl = String(documentationData.lattes_url ?? '').trim();
		const rqe = String(documentationData.rqe ?? '').trim();
		const videoUrl = String(contactData.video_url ?? '').trim();
		const briefDescription = String(
			contactData.brief_description ?? '',
		).trim();
		const mainApproach = String(modalityData.main_approach ?? '').trim();

		const requestBody: any = {
			clinic_id: modalityData.clinic_id || null,
			service_modality: modalityData.service_modality,
			...(mainApproach ? { main_approach: mainApproach } : {}),
			contract: 'INDEPENDENT', // Campo novo
			first_name: personalData.first_name,
			last_name: personalData.last_name,
			...(prefix ? { name_prefix: prefix } : {}),
			...(personalData.gender &&
			personalData.gender !== 'PREFIRO_NAO_INFORMAR'
				? { gender: personalData.gender }
				: {}),
			password: personalData.password,
			...(phoneNumber ? { phone_number: phoneNumber } : {}),
			birthday: personalData.birthday, // ISO vindo do StepOne
			service_ids: [documentationData.profession], // ID da profissão selecionada no StepTwo
			allow_returns: false, // Campo novo
			cpf_or_cnpj: documentationData.cpf || documentationData.cnpj || '',
			crm_or_crp: documentationData.council_number, // Campo unificado do StepTwo
			document_state: documentationData.document_state,
			...(rqe ? { rqe } : {}),
			pos_degree: false, // Conforme exemplo
			...(lattesUrl ? { lattes_url: lattesUrl } : {}),
			...(videoUrl ? { video_url: videoUrl } : {}),
			...(briefDescription
				? { brief_description: briefDescription }
				: {}),
		};

		const professional = await mutateAsync({ body: requestBody });

		if (professional) {
			setProfessionalData(professional);
		}
	};

	const optionsModality = [
		{ value: 'FULL_REMOTE', label: 'Online' },
		{ value: 'HYBRID', label: 'Híbrido' },
		{ value: 'FULL_PRESENCIAL', label: 'Presencial' },
	];

	const iconCommonClass = 'w-4 h-4 text-stone-400';

	const selectedClinic = data?.data.find(
		(clinic: any) => clinic.id === watch('clinic_id'),
	);
	const selectedApproach = getApproach(watch('main_approach'));

	return (
		<div className="flex min-h-screen flex-col overflow-y-auto bg-[#f8f9fa] font-sans">
			<Helmet>
				<title>Modalidade | Plumaa</title>
			</Helmet>
			<Header />

			<main className="flex flex-1 items-center justify-center p-2">
				<div className="flex w-full max-w-[440px] flex-col gap-4 rounded-[2rem] bg-white p-5 shadow-sm md:p-8">
					<div className="space-y-0.5 text-center">
						<h1 className="text-xl font-black tracking-tight text-stone-900 md:text-2xl">
							Modalidade de Atendimento
						</h1>
						<p className="text-[10px] font-medium text-stone-400 md:text-xs">
							Etapa 4 de 4: Escolha como irá atender
						</p>
					</div>

					<form
						onSubmit={handleSubmit(onSubmit)}
						className="space-y-5"
					>
						<div className="space-y-4">
							{/* Abordagem principal (obrigatória para psicólogos) */}
							{isPsychology && (
								<div className="animate-in fade-in slide-in-from-top-1 duration-300">
									<FormLabel htmlFor="field-main-approach">
										Abordagem principal
									</FormLabel>
									<InputWrapperAuth
										icon={
											<Brain
												className={iconCommonClass}
											/>
										}
										error={errors.main_approach?.message}
										value={watch('main_approach')}
										displayError
									>
										<Select
											value={watch('main_approach') ?? ''}
											onValueChange={(value) => {
												setValue(
													'main_approach',
													value as FormData['main_approach'],
												);
												clearErrors('main_approach');
											}}
											className="!w-full"
										>
											<SelectTrigger
												id="field-main-approach"
												className="h-9 w-full border-none bg-transparent p-0 text-sm font-normal shadow-none focus:ring-0"
											>
												<SelectValue>
													{selectedApproach ? (
														<span className="text-stone-600">
															{
																selectedApproach.label
															}
														</span>
													) : (
														<span className="text-stone-400">
															Escolha a abordagem
														</span>
													)}
												</SelectValue>
											</SelectTrigger>
											<SelectContent>
												<SelectGroup>
													{APPROACHES.map(
														(approach) => (
															<SelectItem
																key={
																	approach.key
																}
																value={
																	approach.key
																}
															>
																{approach.label}
															</SelectItem>
														),
													)}
												</SelectGroup>
											</SelectContent>
										</Select>
									</InputWrapperAuth>
									<p className="ml-1 mt-5 text-[10px] text-stone-400">
										{selectedApproach?.hint ??
											'Linha teórica que orienta o seu trabalho. Aparece no seu card.'}
									</p>
								</div>
							)}

							{/* Modalidade */}
							<div>
								<FormLabel>Modalidade</FormLabel>
								<SlidingToggle
									options={optionsModality}
									value={watch('service_modality')}
									onChange={(val) => {
										setValue(
											'service_modality',
											val as FormData['service_modality'],
										);
										clearErrors('service_modality');
									}}
								/>
							</div>

							{/* Clínica de atendimento */}
							{data &&
								watch('service_modality') !== 'FULL_REMOTE' && (
									<div className="animate-in fade-in slide-in-from-top-1 duration-300">
										<FormLabel htmlFor="field-clinic">
											Clínica de atendimento
										</FormLabel>
										<InputWrapperAuth
											icon={
												<IdCard
													className={iconCommonClass}
												/>
											}
											error={errors.clinic_id?.message}
											value={watch('clinic_id')}
											displayError
										>
											<Select
												value={watch('clinic_id')}
												onValueChange={(value) => {
													setValue(
														'clinic_id',
														value,
													);
													clearErrors('clinic_id');
												}}
												className="!w-full"
											>
												<SelectTrigger
													id="field-clinic"
													className="h-9 w-full border-none bg-transparent p-0 text-sm font-normal shadow-none focus:ring-0"
												>
													<SelectValue>
														{selectedClinic?.name ? (
															<span className="text-stone-600">
																{
																	selectedClinic.name
																}
															</span>
														) : (
															<span className="text-stone-400">
																Escolha a
																clínica
															</span>
														)}
													</SelectValue>
												</SelectTrigger>
												<SelectContent>
													<SelectGroup>
														{data?.data.map(
															(clinic: any) => (
																<SelectItem
																	key={
																		clinic.id
																	}
																	value={
																		clinic.id
																	}
																>
																	{
																		clinic.name
																	}
																</SelectItem>
															),
														)}
													</SelectGroup>
												</SelectContent>
											</Select>
										</InputWrapperAuth>
									</div>
								)}
						</div>

						<div className="space-y-4 pt-2">
							<Button
								type="submit"
								className="w-full justify-center rounded-lg border-none bg-[#1a1a1a] py-3 text-sm font-bold text-white transition-all hover:bg-stone-800"
								text="Finalizar Cadastro"
								endIcon={
									<ArrowRightCircle
										size={14}
										className="ml-1"
									/>
								}
							/>

							<div className="text-center">
								<button
									type="button"
									onClick={() => navigate(-1)}
									className="mx-auto flex items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-widest text-stone-400 transition-colors hover:text-stone-900"
								>
									<ArrowLeft size={12} />
									Voltar para etapa anterior
								</button>
							</div>
						</div>

						<div className="mt-2 flex items-center justify-center gap-2 border-t border-stone-50 pt-4 text-[9px] font-black uppercase tracking-widest text-stone-300">
							<ShieldCheck size={14} />
							<span>Seus dados estão protegidos pela Plumaa</span>
						</div>
					</form>
				</div>
			</main>
		</div>
	);
};

export { StepFour };
